import React, {useState} from 'react'
import gql from 'graphql-tag'
import {Checkbox} from 'antd'
import PhotoGallery from 'components/shared/PhotoGallery'
import Query from 'components/shared/Query'
import SelectablePhotoThumb from 'components/shared/SelectablePhotoThumb'
import Button from 'components/shared/Button'
import {sumBy, formatKm} from 'lib/util'

const PER_PAGE = 12

const getAreaPhotosQuery = gql`
  query EstateAreaPhotos($estateId: ID!, $limit: Int!, $page: Int!, $season: String!, $query: String!) {
    estate(id: $estateId) {
      id
      availableAreaPhotos(first: $limit, page: $page, season: $season, search: $query) {
        edges {
          node {
            id
            ...on AreaPhotoAsset {
              name
              unitPrice
              distanceInKm
              image(size: "monitor") { url }
              thumb: image(size: "thumb") { url }
            }
          }
        }
      }
    }
  }
`

const SearchResults = ({
  estateId,
  searchQuery,
  searchSeason,
  selectedPhotos,
  selectedPhotosReducer,
}) => {
  const [photoPreviewIndex, setPhotoPreviewIndex] = useState(null)

  const previewPhoto = (index) => setPhotoPreviewIndex(index)
  const closeGallery = () => setPhotoPreviewIndex(null)

  const photoIsSelected = (photo) => {
    return !!selectedPhotos.find(p => p.id === photo.id)
  }

  const photoSelectedChanged = (photo, selected) => {
    return selectedPhotosReducer({type: selected ? 'add' : 'remove', photo})
  }

  const getGalleryCaption = (photo) => {
    const total = sumBy(selectedPhotos, p => p.unitPrice)

    return (
      <div className="area-photos-gallery-caption">
        <span className="total-photos">
          {selectedPhotos.length > 0 && (
            <>{selectedPhotos.length} bilder valgt ({total}kr)</>
          )}
        </span>
        <span className="toggle-photo">
          <label>
            Velg (+{photo.unitPrice}kr)
            <Checkbox
              checked={photoIsSelected(photo)}
              onChange={(e) => photoSelectedChanged(photo, e.target.checked)}
            />
          </label>
        </span>
      </div>
    )
  }

  return (
    <Query
      inlineLoading
      inlineError
      query={getAreaPhotosQuery}
      variables={{
        estateId,
        page: 1,
        limit: PER_PAGE,
        season: searchSeason,
        query: searchQuery,
      }}
    >
      {({getData, fetchMore}) => {
        const photos = getData('estate.availableAreaPhotos.edges', []).map(e => e.node)
        const canFetchMore = photos.length >= PER_PAGE && photos.length % PER_PAGE === 0

        return (
          <>
            <SelectablePhotoThumb.Wrapper>
              {photos.map((photo, i) => {
                const selected = photoIsSelected(photo)
                return (
                  <SelectablePhotoThumb
                    key={`${photo.id}-${i}`}
                    photo={photo}
                    selected={selected}
                    onChange={(selected) => photoSelectedChanged(photo, selected)}
                    onClick={() => previewPhoto(i)}
                    title={
                      <>
                        <span className="title" title={photo.name}>{photo.name}</span>
                        <span className="distance">{formatKm(photo.distanceInKm)}</span>
                      </>
                    }
                    label={selected ? null : <>Velg ({photo.unitPrice}kr)</>}
                  />
                )
              })}
            </SelectablePhotoThumb.Wrapper>

            {canFetchMore && (
              <div className="fetch-more">
                <Button
                  onClick={() => {
                    const page = Math.max(Math.ceil(photos.length / PER_PAGE + 1), 1)

                    fetchMore({
                      variables: {page},
                      updateQuery: (prev, {fetchMoreResult}) => {
                        if (!fetchMoreResult) return prev
                        return {
                          estate: {
                            ...prev.estate,
                            availableAreaPhotos: {
                              __typename: prev.estate.availableAreaPhotos.__typename,
                              edges: [
                                ...prev.estate.availableAreaPhotos.edges,
                                ...fetchMoreResult.estate.availableAreaPhotos.edges,
                              ]
                            },
                          },
                        }
                      }
                    })
                  }}
                >
                  Vis flere bilder
                </Button>
              </div>
            )}

            {photoPreviewIndex !== null && (
              <PhotoGallery
                images={photos.map(p => p.image.url)}
                initialImageIndex={photoPreviewIndex}
                onRenderCaption={(index) => getGalleryCaption(photos[index])}
                onCloseRequest={closeGallery}
              />
            )}
          </>
        )
      }}
    </Query>
  )
}

export default SearchResults
